import React, { useEffect, useState } from 'react'
import Breadcrumbs from '../../../components/breadcrumbs/Breadcrumbs'
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import AddModel from '../../../components/Add/AddModel';
import AddInputs from '../../../components/Add/AddInputs';
import AddBottomButton from '../../../components/Add/AddBottomButton';
import useApi from '../../../services/useApi';
import S3ImageUpload from '../../../components/S3uploads/S3ImageUpload';
import TextArea from '../../../components/inputfields/TextArea';
import { useNavigate } from "react-router";
import { showToast } from '../../../components/toastMessage/toastSlice';
import { useDispatch } from 'react-redux';

const getScreenResolution = (): { width: number; height: number } => {
    return {
      width: window.screen.width,
      height: window.screen.height,
    };
  };

export default function AddReversalTransaction() {
    const { getAPI, PostAPI } = useApi();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const pathname = useSelector((state: RootState) => state.selectedBankId.listOfUsers);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [mnoAccounts, setMnoAccounts] = useState<Array<{ id: string, name: string }>>([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [searchedTerm, setSearchedTerm] = useState('');
    const [formData, setFormData] = useState({
        account_id: "",
        account_name: "",
        amount: "",
        description: "",
        receipt_url: ""
    });
    const [isXlOrGreater, setIsXlOrGreater] = useState(window.innerWidth >= 1280);

    useEffect(() => {
        const handleResize = () => {
            setIsXlOrGreater(window.innerWidth >= 1280);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    const fetchMnoAccounts = async (searchTerm: string = '', pageNumbeChange?: boolean) => {
        try {
            if (isLoading) return;
            setIsLoading(true);

            const prevSearchTerm = searchedTerm;
            const prevPage = currentPage;
            let pageToFetch = currentPage;

            if (searchTerm !== prevSearchTerm) {
                pageToFetch = 1;
                setCurrentPage(1);
                setSearchedTerm(searchTerm);
            } else if (pageNumbeChange && prevSearchTerm === searchTerm && prevPage === currentPage) {
                pageToFetch = currentPage + 1;
                setCurrentPage(pageToFetch);
            }

            const response = await getAPI(`manual-entries/accounts?type=mno-reversal&page_number=${pageToFetch}&search=${searchTerm}`);

            if (response.data) {
                const mnoList = response.data.data || [];
                const newAccounts = mnoList.map((item: { id: string, name: string }) => ({
                    id: item.id,
                    name: item.name
                }));

                setMnoAccounts((prevAccounts) => {
                    if (searchTerm !== prevSearchTerm) {
                        return newAccounts;
                    }
                    const uniqueAccounts = newAccounts.filter((newAccount: any) =>
                        !prevAccounts.some((prevAccount) => prevAccount.id === newAccount.id)
                    );
                    return [...prevAccounts, ...uniqueAccounts];
                });
            }
        } catch (err) {
            console.error("Error fetching accounts:", err);
            setErrors(prev => ({ ...prev, account_id: "Failed to fetch accounts" }));
        } finally {
            setIsLoading(false);
        }
    };
    const targetInputs = {
        "": {
            "MNO Inventory Account": {
                required: true,
                id: "mno_inventory_account",
                type: "dropDown",
                options: mnoAccounts.map(account => account.name),
                tabIndex: 0,
                noSearch: false,
                disabled: false,
                loading: isLoading,
            },
            Amount: {
                required: true,
                id: "amount",
                type: "currency",
                tabIndex: 1,
                percentage: true,
                disabled: false,
            },
        },
    };
    const handleInputChange = (id: string, value: string) => {
        if (id === 'mno_inventory_account') {
            const selectedAccount = mnoAccounts.find(account => account.name === value);
            if (selectedAccount) {
                setFormData(prev => ({
                    ...prev,
                    account_id: selectedAccount.id,
                    account_name: selectedAccount.name
                }));
            }
        } else {
            setFormData(prev => ({ ...prev, [id]: value }));
        }

        if (errors[id]) {
            setErrors(prev => ({ ...prev, [id]: "" }));
        }
    };
    const handleFileUpload = (url: string, value: string) => {
        setFormData(prev => ({ ...prev, receipt_url: value }));
    };

    const handleSubmit = async () => {
        const newErrors: Record<string, string> = {};

        if (!formData.account_id) newErrors.mno_inventory_account = "This field is required";
        if (!formData.amount) { 
            newErrors.amount = "This field is required" 
        } else if (Number(formData.amount) < 0.01) { 
            newErrors.amount = "Amount must be greater than AFA 0.01" 
        };
        if (!formData.description) newErrors.description = "This field is required";

        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            try {
                setLoading(true);

                const payload = {
                    account_id: formData.account_id,
                    account_name: formData.account_name,
                    amount: parseFloat(formData.amount),
                    description: formData.description,
                    receipt_url: formData.receipt_url
                };

                const response = await PostAPI('manual-entries/mno-reversal', payload);

                if (!response.error) {
                    dispatch(showToast({ type: "success", message: "Reversal added successfully" }));
                    navigate("/zoodpay-money/reversals/recharge-reversal");
                    setFormData({
                        account_id: "",
                        account_name: "",
                        amount: "",
                        description: "",
                        receipt_url: ""
                    });
                    console.log('Reversal transaction created successfully');
                } else {
                    console.error('Error creating reversal transaction:', response.data);
                }
            } catch (error) {
                console.error('Error submitting form:', error);
            } finally {
                setLoading(false);
            }
        }
    };
    useEffect(() => {
        fetchMnoAccounts(undefined, false);
    }, []);
    return (
        <>
            <Breadcrumbs
                navPath={[
                    "Zoodpay Money",
                    "Reversals",
                    "List of Reversals",
                    "Add Reversal",
                ]}
                links={[
                    pathname ? pathname : "/zoodpay-money/reversals/recharge-reversal",
                    pathname ? pathname : "/zoodpay-money/reversals/recharge-reversal",
                    pathname ? pathname : "/zoodpay-money/reversals/recharge-reversal",
                ]}
                heading={`Add Reversal`}
            />
            <AddModel>
                <AddInputs
                    mapping={true}
                    halfWidth={!isXlOrGreater}
                    index={2}
                    errors={errors}
                    totalInputCount={2}
                    onInputChange={handleInputChange}
                    inputs={targetInputs}
                    inputValues={{
                        mno_inventory_account: formData.account_name,
                        amount: formData.amount,
                    }}
                    loadMoreOptions={(searchTerm: string, pageNumbeChange?: boolean) =>
                        fetchMnoAccounts(searchTerm, pageNumbeChange)
                    }
                />
                <S3ImageUpload
                    heading=""
                    label="Receipt (if any)"
                    disabled={false}
                    upload_id="receipt_url"
                    UploadPath="uploads/receipt_url"
                    handleUpload={handleFileUpload}
                    uploadedFile={formData.receipt_url}
                    errorMessage={""}
                    required={false}
                    id="receipt_url"
                    index={1}
                    paddingTop={
                        !errors.mno_inventory_account
                            ? "pt-2"
                            : "relative top-[-18px]"
                    }
                    customWidth={'w-[45%] xl:w-[30.8%]'}
                />
                <TextArea
                    label="Description"
                    required={true}
                    placeholderRequied={false}
                    text={formData.description}
                    setText={(value: any) => handleInputChange("description", value)}
                    textError={errors.description || ""}
                    setTextError={() => { }}
                    bgColor={"bg-white"}
                    maxLength={250}
                    disabled={false}
                    top={!errors.mno_inventory_account ? "pt-5" : "pt-[2px]"}
                />
            </AddModel>
            <AddBottomButton
                buttonWidth={"w-[140px]"}
                buttonName={`Add Reversal`}
                buttonClass={`w-[140px] whitespace-nowrap`}
                onClick={handleSubmit}
                disabled={loading}
                loading={loading}
            />
        </>
    );
}